import React, { useRef, useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import DataGrid, {
    Column, Selection, Scrolling, Editing, Export, ColumnChooser, ColumnChooserSearch, Toolbar, Item,
    RequiredRule, StateStoring,
} from "devextreme-react/data-grid";
import { createStore } from "devextreme-aspnet-data-nojquery";
import { Button, SelectBox, TextArea } from "devextreme-react";
import { confirm } from "devextreme/ui/dialog";
import axios from "axios";
import RenderSelectBoxCell from "../../common/RenderCell";
import data from "../../assets/apiRoutes.json";
import {
    SHIPMENT_TRANSACTION_TYPES, AVAILABLE_FLAG_YES, AVAILABLE_FLAG_NO, REASON_CODE_TYPE, TRANSACTION_TYPE_DAMAGED,
} from "../itemManagement/transactionsTab/codeLists";
import { EMPTY_GUID } from "../../const/constants";
import GridLayoutManager from "../../gridLayoutCustomization/GridLayoutManager";
import { GridLayout } from "../../common/GridLayout";

const money = { type: "fixedPoint", precision: 2, useGrouping: true };
const LOAD_URL = data.IM.GetTransactionlocationDetails;
const INSERT_URL = data.IM.AddTransactionlocationDetails;
const UPDATE_URL = data.IM.UpdateTransactionlocation;
const DELETE_URL = data.IM.DeleteTransactionlocation;

// Saved-layout key for this grid. Distinct from the detail grid above it — the two grids on the
// Details tab store their layouts separately. Must stay stable; layouts are keyed on it.
const gridKey = "0f4a83c6-57d1-4e9b-8b62-1c7a25e0fd48";

// Details/Primary sub-tab location breakdown (Delphi uIMD0019): the IMTRANSACTIONLOCATION rows for the
// SELECTED detail line, with inline add/update + delete — reusing the same im/Transactionlocation
// endpoints (detail id sent as the `Key` header) that the WM transaction Primary sub-tab uses.
function DetailLocationGrid(props) {
    const accessToken = props.oidc?.user?.access_token;
    const detailRowId = props.detailRowId;
    const editable = !props.disabled && detailRowId && detailRowId !== EMPTY_GUID;
    const dataGrid = useRef(null);
    const [selectedKeys, setSelectedKeys] = useState([]);

    // Grid layout customization, same wiring as the detail grid above and the WM Transaction Data
    // Entry detail tab: state is persisted per user and per named layout, not in browser storage.
    const [currentLayout, setCurrentLayout] = useState("Default");
    const saveState = useCallback((state) => {
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout);
    }, [accessToken, currentLayout]);
    const loadState = useCallback(() => {
        return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout);
    }, [accessToken, currentLayout]);

    // Receipt-type transactions record into IN RECEIVING; usage/shipment into OUT SHIPPED (matches the
    // WM reference's transactionType switch).
    const qtyField = SHIPMENT_TRANSACTION_TYPES.includes(props.transactionType) ? "outShipped" : "inReceiving";

    // Keep the store stable; read the current line id via a ref so the grid doesn't rebuild every render.
    const detailRowIdRef = useRef(detailRowId);
    detailRowIdRef.current = detailRowId;

    const notifyChanged = useCallback(() => { if (props.onChanged) props.onChanged(); }, [props.onChanged]);

    const dataSource = useMemo(() => createStore({
        key: "rowid",
        loadUrl: `${window._env_.REACT_APP_APIS}/${LOAD_URL}`,
        insertUrl: `${window._env_.REACT_APP_APIS}/${INSERT_URL}`,
        updateUrl: `${window._env_.REACT_APP_APIS}/${UPDATE_URL}`,
        onBeforeSend: (method, ajaxOptions) => {
            ajaxOptions.headers = {
                Authorization: `Bearer ${accessToken}`,
                Key: detailRowIdRef.current || EMPTY_GUID,
            };
        },
        onInserted: notifyChanged,
        onUpdated: notifyChanged,
    }), [accessToken, notifyChanged]);

    const onInitNewRow = (e) => {
        if (!e.data.availableflag) {
            e.data.availableflag = AVAILABLE_FLAG_YES;
            e.data.availableflagDiscription = "Yes";
        }
    };

    // With cell editing, clicking a cell starts editing but doesn't select the row; select it so the
    // trash button enables (mirrors the WM Primary sub-tab location grid).
    const onEditingStart = (e) => {
        const grid = dataGrid.current?.instance();
        if (e.key != null && grid && !grid.isRowSelected(e.key)) {
            grid.selectRows([e.key], false);
        }
    };

    const renderAvailableCell = (cellElement) => {
        const items = [{ id: AVAILABLE_FLAG_YES, name: "Yes" }, { id: AVAILABLE_FLAG_NO, name: "No" }];
        return (
            <SelectBox
                dataSource={items}
                valueExpr="id"
                displayExpr="name"
                defaultValue={cellElement.row.data.availableflag}
                // Delphi EnableDisableLocation: the Available flag is disabled entirely for Damaged/Unavailable (type 'M').
                disabled={props.transactionType === TRANSACTION_TYPE_DAMAGED}
                onValueChanged={(e) => {
                    // Hand both the code and the display text to setCellValue via its payload (same as the WH
                    // and Location columns) instead of a module-level var shared across every mounted grid.
                    cellElement.setValue({ code: e.value, text: e.component.option("displayValue") });
                }}
            />
        );
    };
    const setAvailableValue = (rowData, newValue) => {
        const code = newValue ? newValue.code : "";
        if (code === AVAILABLE_FLAG_YES) rowData.availablereasoncode = "";
        rowData.availableflag = code;
        rowData.availableflagDiscription = newValue ? newValue.text : "";
    };

    const renderCommentCell = (cellElement) => (
        <TextArea labelMode="static" stylingMode="outlined" onValueChanged={(e) => cellElement.setValue(e.value)} />
    );

    const deleteRecords = useCallback(() => {
        confirm("<i>Delete selected record?</i>", "Delete").then((ok) => {
            if (!ok) return;
            const grid = dataGrid.current?.instance();
            if (grid) grid.beginCustomLoading("Deleting");
            axios.post(`${window._env_.REACT_APP_APIS}/${DELETE_URL}`, selectedKeys, {
                headers: { "Content-Type": "application/json", access_token: accessToken },
            }).then(() => {
                if (grid) { grid.endCustomLoading(); grid.refresh(); }
                setSelectedKeys([]);
                notifyChanged();
            }).catch(() => { if (grid) grid.endCustomLoading(); });
        });
    }, [selectedKeys, accessToken, notifyChanged]);

    return (
        <DataGrid
            ref={dataGrid}
            dataSource={dataSource}
            selectedRowKeys={selectedKeys}
            onSelectionChanged={(d) => setSelectedKeys(d.selectedRowKeys)}
            onInitNewRow={onInitNewRow}
            onEditingStart={onEditingStart}
            showBorders={true}
            columnAutoWidth={true}
            height="100%"
            noDataText={editable ? "No locations" : "Select a detail line"}
        >
            <StateStoring
                enabled={true}
                type="custom"
                customLoad={loadState}
                storageKey={gridKey}
                customSave={saveState}
            />
            <Scrolling mode="standard" />
            <Selection mode="single" />
            <Export enabled={true} />
            <ColumnChooser enabled={true} title="Choose Column" width={290} height={290} sortOrder="asc">
                <ColumnChooserSearch enabled={true} editorOptions={{ placeholder: "Search column" }} />
            </ColumnChooser>
            <Editing mode="cell" refreshMode="repaint" allowUpdating={editable} allowAdding={editable} />

            <Column dataField="rowid" visible={false} showInColumnChooser={false} />
            <Column dataField="keySequence" caption="Key Sequence" visible={false} showInColumnChooser={false} />
            <Column
                dataField="warehousecode"
                caption="WH"
                editCellRender={(cell) => RenderSelectBoxCell({
                    loadUrl: `${window._env_.REACT_APP_APIS}/${data.ID.GetWarehouses}`,
                    key: "id", displayExpr: "name", valueExpr: "id", accessToken,
                    defaultValue: cell.data.warehouseId,
                    sort: [{ selector: "name", desc: false }],
                    onValueChangeCallback: (value, text, additionalData) => cell.setValue({ value, text, additionalData }),
                })}
                setCellValue={(rowData, newValue) => { rowData.warehouseId = newValue.value; rowData.warehousecode = newValue.text; }}
            />
            <Column
                dataField="locationcode"
                caption="Location"
                editCellRender={(cell) => RenderSelectBoxCell({
                    loadUrl: `${window._env_.REACT_APP_APIS}/${data.IM.Lookup.GetLocations}`,
                    key: "id", displayExpr: "name", valueExpr: "id", accessToken,
                    loadParams: { warehouseRowId: cell.data.warehouseId },
                    defaultValue: cell.data.locationId,
                    sort: [{ selector: "name", desc: false }],
                    onValueChangeCallback: (value, text, additionalData) => cell.setValue({ value, text, additionalData }),
                })}
                setCellValue={(rowData, newValue) => { rowData.locationId = newValue.value; rowData.locationcode = newValue.text; }}
            >
                <RequiredRule message="A valid Location must be assigned" />
            </Column>
            <Column dataField={qtyField} caption="Quantity" dataType="number" format={money} />
            <Column dataField="availableflagDiscription" caption="Available" editCellRender={renderAvailableCell} setCellValue={setAvailableValue} />
            <Column
                dataField="availablereasoncode"
                caption="Reason"
                dataType="string"
                editCellRender={(cell) => RenderSelectBoxCell({
                    // IMCODES, not WMCODES: wm/Lookup/GetCodes resolves LookupType.WMCode, which is a
                    // different table from the IMCODES list Delphi reads here.
                    loadUrl: `${window._env_.REACT_APP_APIS}/${data.Common.GetLookup}`,
                    loadParams: { module: "im", lookupType: "ImCode", codetype: REASON_CODE_TYPE, showLookupGrid: false },
                    key: "name", displayExpr: "name", valueExpr: "name", accessToken,
                    disabled: cell.row.data.availableflag === AVAILABLE_FLAG_YES,
                    sort: [{ selector: "name", desc: false }],
                    defaultValue: cell.data.availablereasoncode,
                    onValueChangeCallback: (value, text) => cell.setValue({ value, text }),
                })}
                setCellValue={(rowData, newValue) => { rowData.availablereasoncode = newValue.text; }}
            />
            <Column dataField="comment" caption="Comment" visible={false} editCellRender={renderCommentCell}
                setCellValue={(rowData, newValue) => { rowData.comment = newValue; }} />

            <Toolbar>
                <Item name="addRowButton" visible={editable} />
                <Item location="after">
                    <Button icon="trash" onClick={deleteRecords} disabled={!editable || !selectedKeys.length} />
                </Item>
                <Item location="after">
                    <GridLayoutManager gridKey={gridKey} dataGrid={dataGrid} setCurrentLayout={setCurrentLayout} />
                </Item>
                <Item name="exportButton" />
                <Item name="columnChooserButton" />
            </Toolbar>
        </DataGrid>
    );
}

function mapStateToProps(state) {
    return { oidc: state.oidc };
}

export default connect(mapStateToProps)(DetailLocationGrid);
